import {
  Button,
  Checkbox,
  Flex,
  Tooltip,
  Typography,
  useToast,
} from '@flex-design-system/fx';
import { PlusIcon } from '@flex-design-system/legacy-flex-icons';
import { Translation, useTranslation } from '@flex-packages/i18n';
import type { CandidateWorkExperienceBackground } from '@flex-private-packages/recruiting-common/base';
import { CandidateBackgroundList } from '@flex-private-packages/recruiting-common/components';
import { DndKit, useDndKitItem } from '@flex-services/shared-ui/layout';
import { useState } from 'react';
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { getMobileStyle, mobileOnly, pcOnly } from '../../../../../utils';
import type { JobDescriptionFormState } from '../../../models';
import {
  BackgroundItemContentColumn,
  BackgroundItemContentRow,
  FormSection,
} from '../../../shared';

import {
  WorkExperienceCompanyNameAndStatusFormItem,
  WorkExperienceContractTypeSelectFormItem,
  WorkExperienceJobRoleInputFormItem,
  WorkExperienceJoinYearMonthInputFormItem,
  WorkExperienceRetirementYearMonthInputFormItem,
} from './form-items';
import Information from './Information';

const FIRST_ITEM_INDEX = 0;

export function WorkExperienceBackgroundSection() {
  const [selectedItemIndex, setSelectedItemIndex] = useState<
    number | undefined
  >(0);

  const { control, setValue } = useFormContext<JobDescriptionFormState>();
  const noWorkExperience = useWatch({ control, name: 'noWorkExperience' });
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'workExperienceBackgrounds',
  });

  const handleChangeSelectedItemIndex = (newIndex: number) => {
    if (newIndex === selectedItemIndex) {
      setSelectedItemIndex(undefined);

      return;
    }

    setSelectedItemIndex(newIndex);
  };

  const handleAddEducationalBackgroundItem = () => {
    append({} as any);
    setSelectedItemIndex(fields.length);
  };

  const handleRemoveEducationalBackgroundItem = (targetIndex: number) => {
    remove(targetIndex);
  };

  return (
    <FormSection
      title={
        <FormSection.Title
          subtext={
            <FormSection.Subtext
              css={{
                ...getMobileStyle({
                  whiteSpace: 'pre-line',
                }),
              }}
            >
              <Translation tKey="recruiting.work_experience.subtext" />
            </FormSection.Subtext>
          }
          rightSlot={
            <Flex direction="row" align="center" css={{ gap: 16 }}>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <Checkbox.Label>
                    <Checkbox
                      color="black"
                      checked={noWorkExperience}
                      onCheckedChange={(newChecked: boolean) =>
                        setValue('noWorkExperience', newChecked)
                      }
                    />
                    <Typography asChild>
                      <Translation tKey="recruiting.work_experience.empty" />
                    </Typography>
                  </Checkbox.Label>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <Translation tKey="recruiting.work_experience.empty.description" />
                </Tooltip.Content>
              </Tooltip>

              <Button
                size="small"
                variant="outline"
                color="gray"
                leftSlot={<PlusIcon color="primary" />}
                onClick={handleAddEducationalBackgroundItem}
                css={pcOnly}
                disabled={noWorkExperience}
              >
                <Translation tKey="recruiting.background_section.add_button" />
              </Button>
            </Flex>
          }
        >
          <Translation tKey="recruiting.work_experience.placeholder" />
        </FormSection.Title>
      }
    >
      {!noWorkExperience && (
        <Flex direction="column" align="stretch" css={{ gap: 10 }}>
          <CandidateBackgroundList>
            <DndKit.Group
              items={fields}
              getIdentifier={field => field.id}
              onReorder={(_, oldIndex, newIndex) => {
                move(oldIndex, newIndex);
              }}
              dndContextProps={{
                onDragStart: () => {
                  setSelectedItemIndex(undefined);
                },
              }}
              axis="y"
            >
              {fields.map((value, currentIndex) => {
                return (
                  <ListItem
                    key={value.id}
                    id={value.id}
                    currentIndex={currentIndex}
                    isExpanded={selectedItemIndex === currentIndex}
                    workExperienceBackgrounds={value}
                    handleChangeSelectedItemIndex={
                      handleChangeSelectedItemIndex
                    }
                    handleRemoveEducationalBackgroundItem={
                      handleRemoveEducationalBackgroundItem
                    }
                  />
                );
              })}
            </DndKit.Group>
          </CandidateBackgroundList>
          <Button
            size="large"
            variant="outline"
            color="gray"
            leftSlot={<PlusIcon color="primary" />}
            onClick={handleAddEducationalBackgroundItem}
            css={{ ...mobileOnly }}
          >
            <Translation tKey="recruiting.background_section.add_button" />
          </Button>
        </Flex>
      )}
    </FormSection>
  );
}

function ListItem({
  id,
  currentIndex,
  workExperienceBackgrounds,
  isExpanded,
  handleChangeSelectedItemIndex,
  handleRemoveEducationalBackgroundItem,
}: {
  id: string;
  currentIndex: number;
  workExperienceBackgrounds: CandidateWorkExperienceBackground;
  isExpanded: boolean;
  handleChangeSelectedItemIndex: (newIndex: number) => void;
  handleRemoveEducationalBackgroundItem: (index: number) => void;
}) {
  const { t } = useTranslation();
  const { success } = useToast();

  const { control } = useFormContext<JobDescriptionFormState>();
  const {
    fieldState: { error },
  } = useController({
    control,
    name: `workExperienceBackgrounds.${currentIndex}`,
    rules: {
      validate: value => {
        // TODO: 각 Filed의 rule과 연동할 수 있는 방안 생각하기
        return Boolean(
          value.name &&
            value.workExperienceStatus &&
            value.contractType &&
            value.joinDate
        );
      },
    },
  });

  const { a11yAttributes, listeners, setNodeRef, style } = useDndKitItem({
    id,
  });

  return (
    <CandidateBackgroundList.Item
      {...a11yAttributes}
      danger={Boolean(error)}
      ref={setNodeRef}
      expanded={isExpanded}
      style={style}
      onCollapse={() => handleChangeSelectedItemIndex(currentIndex)}
      header={
        <CandidateBackgroundList.ItemHeader
          leftSlot={
            <CandidateBackgroundList.ItemHeader.DragButton {...listeners} />
          }
          centerSlot={
            <Information
              companyName={workExperienceBackgrounds.name}
              workExperienceStatus={
                workExperienceBackgrounds.workExperienceStatus
              }
              isLastBackground={currentIndex === FIRST_ITEM_INDEX}
            />
          }
          rightSlot={
            <CandidateBackgroundList.ItemHeader.MoreDropdown
              deleteTitle={t('recruiting.work_experience.delete.confirm.title')}
              onItemDelete={() => {
                handleRemoveEducationalBackgroundItem(currentIndex);
                success(t('recruiting.work_experience.delete.success'));
              }}
            />
          }
        />
      }
      content={
        <CandidateBackgroundList.ItemContent>
          <BackgroundItemContentColumn
            direction="column"
            align="stretch"
            css={{ gap: 24 }}
          >
            <WorkExperienceCompanyNameAndStatusFormItem
              currentIndex={currentIndex}
            />
            <BackgroundItemContentRow>
              <WorkExperienceContractTypeSelectFormItem
                currentIndex={currentIndex}
              />
              <WorkExperienceJobRoleInputFormItem currentIndex={currentIndex} />
            </BackgroundItemContentRow>
            <BackgroundItemContentRow>
              <WorkExperienceJoinYearMonthInputFormItem
                currentIndex={currentIndex}
              />
              <WorkExperienceRetirementYearMonthInputFormItem
                currentIndex={currentIndex}
              />
            </BackgroundItemContentRow>
          </BackgroundItemContentColumn>
        </CandidateBackgroundList.ItemContent>
      }
    />
  );
}
